import { createNavigationContainerRef, StackActions } from '@react-navigation/native';
import { abortAllRequests } from '@utils/abortController';
import { store } from '@reduxLocal/persistState';
import { Platform } from 'react-native';

export const navigationRef = createNavigationContainerRef();

// Debug helper - only runs in development
function logNavigationState(actionName) {
    if (__DEV__ && navigationRef.isReady()) {
        try {
            console.log(`[Navigation Debug] ${actionName}`);
            console.log('Current routes:', navigationRef.getRootState()?.routes?.map(r => r.name));
            console.log('Full state:', JSON.stringify(navigationRef.getRootState(), null, 2));
        } catch (e) {
            console.warn('[Navigation Debug] Failed to log state', e);
        }
    }
}

// Helper function to dismiss easy mode if active and not locked in onboarding
async function dismissEasyModeIfNeeded() {
    try {
        const state = store.getState();
        const easyMode = state?.UI?.easyMode;
        const onboardingInProgress = state?.UI?.onboardingInProgress;

        // Only dismiss if easy mode is active and onboarding is NOT in progress
        if (easyMode && !onboardingInProgress) {
            // Check if close handler exists (only on web)
            if (Platform.OS === 'web' && typeof window !== 'undefined' && window.__easyModeCloseHandler) {
                const shouldClose = await window.__easyModeCloseHandler();
                if (!shouldClose) {
                    // User cancelled or dialog was dismissed - navigation should be prevented
                    return false;
                }
                // Handler already dispatched TOGGLE_EASY_MODE, so we don't need to dispatch again
            }
        }
        return true;
    } catch (error) {
        console.warn('[Navigation] Error dismissing easy mode:', error);
        // On error, allow navigation to proceed
        return true;
    }
}

export default async function navigate(name, params) {
    if (navigationRef.isReady()) {
        // Check and dismiss easy mode if needed (and not locked in onboarding)
        const canNavigate = await dismissEasyModeIfNeeded();
        if (!canNavigate) {
            // User cancelled easy mode dismissal, prevent navigation
            if (__DEV__) console.log(`[Navigation] navigate cancelled - easy mode dismissal cancelled`);
            return;
        }

        if (__DEV__) console.log(`[Navigation] navigate -> ${name}`, params);
        navigationRef.navigate(name, params); // Fixed: removed .current - createNavigationContainerRef doesn't use .current
        logNavigationState(`navigate -> ${name}`);
        abortAllRequests();
    } else if (__DEV__) {
        console.warn('[Navigation] navigate called but navigationRef not ready', name);
    }
}

export async function stack(...args) {
    if (navigationRef.isReady()) {
        // Check and dismiss easy mode if needed (and not locked in onboarding)
        const canNavigate = await dismissEasyModeIfNeeded();
        if (!canNavigate) {
            // User cancelled easy mode dismissal, prevent navigation
            if (__DEV__) console.log(`[Navigation] stack push cancelled - easy mode dismissal cancelled`);
            return;
        }

        if (__DEV__) console.log(`[Navigation] stack push`, args);
        navigationRef.dispatch(StackActions.push(...args)); // Fixed: removed .current
        logNavigationState('stack push');
    } else if (__DEV__) {
        console.warn('[Navigation] stack push called but navigationRef not ready', args);
    }
}
